import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { setLogout, setSessionExpired, updateSessionToken } from "../redux/authSlice";
import { useRefreshSessionMutation, useGetSessionConfigQuery } from "../slices/usersApiSlice";
import { notify } from "../utils/notifications";

const STORAGE_KEY_LAST_ACTIVE = "quickpay_session_last_active";
const STORAGE_KEY_LOGOUT_EVENT = "quickpay_session_logout_broadcast";

/**
 * useSessionTimeout
 * Enterprise-grade session inactivity management hook.
 *
 * Requirements & Configuration:
 * - 30 minutes inactivity timeout with 60-second warning countdown across all environments.
 * - Real Wall-Clock Inactivity Tracking: persists timestamps in localStorage so
 *   tab switching, minimized windows, screen locking (Win + L), and system sleep
 *   accurately measure real elapsed time without freezing.
 * - Cross-Tab Synchronization: activity in any tab keeps all tabs alive;
 *   logout in one tab cleanly logs out all open tabs.
 * - In-Flight Protection: defers timeout while critical transactions are processing
 *   with a 2-minute safety cap to prevent permanent lockup from stalled requests.
 * - Payment callback immunity: suppresses timeout on callback/status pages.
 * - Stay Logged In: calls backend refreshSession to renew JWT.
 */
export const useSessionTimeout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { isAuthenticated } = useSelector((state) => state.auth || {});
  const apiMutations = useSelector((state) => state.api?.mutations || {});

  // Fetch optional remote config from backend
  const { data: serverConfig } = useGetSessionConfigQuery(undefined, {
    skip: !isAuthenticated,
  });

  const [refreshSession, { isLoading: isRefreshing }] = useRefreshSessionMutation();

  // Clear any legacy test keys from localStorage
  useEffect(() => {
    try {
      localStorage.removeItem("test_session_timeout_seconds");
      localStorage.removeItem("test_session_warning_seconds");
      localStorage.removeItem("test_session_20s");
    } catch (e) {}
  }, []);

  // Standard duration: 30 minutes timeout with 60 seconds warning (configured via serverConfig or 30m default)
  const effectiveTimeoutSeconds = serverConfig?.timeoutMinutes
    ? serverConfig.timeoutMinutes * 60
    : 30 * 60;

  const effectiveWarningSeconds = serverConfig?.warningSeconds || 60;

  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [countdown, setCountdown] = useState(effectiveWarningSeconds);

  // Use refs to avoid re-triggering effects on state changes
  const isWarningOpenRef = useRef(false);
  isWarningOpenRef.current = isWarningOpen;

  const lastActivityRef = useRef(Date.now());
  const pendingMutationStartTimeRef = useRef(null);
  const intervalTimerRef = useRef(null);
  const apiMutationsRef = useRef(apiMutations);
  apiMutationsRef.current = apiMutations;

  // Helper to get persistent last active timestamp
  const getLastActiveTime = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_LAST_ACTIVE);
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed) && parsed > 0) {
          lastActivityRef.current = parsed;
          return parsed;
        }
      }
    } catch (e) {}
    return lastActivityRef.current;
  }, []);

  // Helper to record activity in memory and localStorage
  const recordActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    try {
      localStorage.setItem(STORAGE_KEY_LAST_ACTIVE, now.toString());
    } catch (e) {}
  }, []);

  // Payment callback routes, status checking, and receipts should NEVER be interrupted by timeout
  const isPaymentCallbackRoute = useCallback(() => {
    const path = (location?.pathname || "").toLowerCase();
    const search = (location?.search || "").toLowerCase();
    return (
      path.includes("/payment/callback") ||
      path.includes("/payment-callback") ||
      path.includes("/payment/status") ||
      path.includes("/payment-status") ||
      (path.includes("/payin") && (search.includes("order_id") || search.includes("orderid"))) ||
      search.includes("urn=")
    );
  }, [location]);

  // Check if any critical API mutation is currently in-flight (with 2-minute safety cap)
  const hasInFlightMutation = useCallback(() => {
    const mutations = apiMutationsRef.current;
    if (!mutations || typeof mutations !== "object") return false;
    const hasPending = Object.values(mutations).some((m) => m && m.status === "pending");

    if (hasPending) {
      if (!pendingMutationStartTimeRef.current) {
        pendingMutationStartTimeRef.current = Date.now();
      }
      // Safety cap: if pending for > 2 minutes, ignore so we don't stall timeout indefinitely
      if (Date.now() - pendingMutationStartTimeRef.current > 120000) {
        return false;
      }
      return true;
    }

    pendingMutationStartTimeRef.current = null;
    return false;
  }, []);

  // Perform Clean Logout on Session Expiry
  const handleSessionTimeout = useCallback(() => {
    setIsWarningOpen(false);
    isWarningOpenRef.current = false;

    if (intervalTimerRef.current) {
      clearInterval(intervalTimerRef.current);
      intervalTimerRef.current = null;
    }

    // Broadcast logout across tabs
    try {
      localStorage.setItem(STORAGE_KEY_LOGOUT_EVENT, Date.now().toString());
    } catch (e) {}

    const reason = "Your session timed out due to inactivity. Please log in again.";

    dispatch(
      setSessionExpired({
        message: reason,
      })
    );
    dispatch(setLogout());

    // Preserve current location so user returns to it post-login
    const currentPath = (location?.pathname || "/dashboard") + (location?.search || "");
    navigate("/login", {
      replace: true,
      state: {
        from: currentPath,
        sessionExpired: true,
        reason,
      },
    });

    notify.info(reason);
  }, [dispatch, navigate, location]);

  // Reset the idle timer to current timestamp
  const resetTimer = useCallback(() => {
    recordActivity();
    if (isWarningOpenRef.current) {
      setIsWarningOpen(false);
      isWarningOpenRef.current = false;
      setCountdown(effectiveWarningSeconds);
    }
  }, [effectiveWarningSeconds, recordActivity]);

  // Record user activity (throttled)
  const handleUserActivity = useCallback(() => {
    // Don't dismiss if warning modal is already actively prompting user to choose
    if (isWarningOpenRef.current) return;

    const now = Date.now();
    if (now - lastActivityRef.current > 1000) {
      recordActivity();
    }
  }, [recordActivity]);

  // Cross-Tab Synchronization via storage events
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleStorageEvent = (e) => {
      if (e.key === STORAGE_KEY_LAST_ACTIVE && e.newValue) {
        const remoteTime = parseInt(e.newValue, 10);
        if (!isNaN(remoteTime)) {
          lastActivityRef.current = remoteTime;
          // If warning was open but user was active in another tab, close warning
          if (isWarningOpenRef.current) {
            setIsWarningOpen(false);
            isWarningOpenRef.current = false;
            setCountdown(effectiveWarningSeconds);
          }
        }
      } else if (e.key === STORAGE_KEY_LOGOUT_EVENT) {
        // Another tab logged out
        dispatch(setLogout());
        navigate("/login", {
          replace: true,
          state: {
            sessionExpired: true,
            reason: "Session closed from another window or tab.",
          },
        });
      }
    };

    window.addEventListener("storage", handleStorageEvent);
    return () => window.removeEventListener("storage", handleStorageEvent);
  }, [isAuthenticated, effectiveWarningSeconds, dispatch, navigate]);

  // Handle Tab Focus & Visibility Change (Wall-Clock evaluation)
  useEffect(() => {
    if (!isAuthenticated) return;

    const evaluateWallClockInactivity = () => {
      const lastActive = getLastActiveTime();
      // If lastActive is invalid or uninitialized (e.g. freshly mounted or cleared), record current time instead of false timeout
      if (!lastActive || isNaN(lastActive) || lastActive <= 0) {
        recordActivity();
        return;
      }
      const idleSeconds = Math.floor((Date.now() - lastActive) / 1000);
      const idleWarningThreshold = effectiveTimeoutSeconds - effectiveWarningSeconds;

      if (idleSeconds >= effectiveTimeoutSeconds) {
        handleSessionTimeout();
      } else if (idleSeconds >= idleWarningThreshold) {
        const remaining = Math.max(0, effectiveTimeoutSeconds - idleSeconds);
        setIsWarningOpen(true);
        isWarningOpenRef.current = true;
        setCountdown(remaining);
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        evaluateWallClockInactivity();
      }
    };

    const handleWindowFocus = () => {
      evaluateWallClockInactivity();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleWindowFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [
    isAuthenticated,
    effectiveTimeoutSeconds,
    effectiveWarningSeconds,
    getLastActiveTime,
    handleSessionTimeout,
    recordActivity,
  ]);

  // Event listeners for user interaction (mounted once per session)
  useEffect(() => {
    if (!isAuthenticated) return;

    // Only set initial timestamp if none exists in localStorage
    if (!localStorage.getItem(STORAGE_KEY_LAST_ACTIVE)) {
      recordActivity();
    }

    // Intentional user actions (clicks, keyboard, touch, scroll)
    const events = ["mousedown", "keydown", "touchstart", "scroll", "click"];
    const handler = () => handleUserActivity();

    events.forEach((evt) => window.addEventListener(evt, handler, { passive: true }));

    return () => {
      events.forEach((evt) => window.removeEventListener(evt, handler));
    };
  }, [isAuthenticated, handleUserActivity, recordActivity]);

  // Handler for user clicking "Stay Logged In"
  const handleStayLoggedIn = useCallback(async () => {
    try {
      const res = await refreshSession().unwrap();
      if (res?.token) {
        dispatch(updateSessionToken({ token: res.token }));
      }
      resetTimer();
      notify.success("Session extended successfully.");
    } catch (err) {
      console.warn("Session refresh request failed:", err);
      // Even if network blips, reset client countdown if user is active
      resetTimer();
    }
  }, [refreshSession, dispatch, resetTimer]);

  // Master Inactivity Interval Checker (runs every 1000ms using absolute wall-clock time)
  useEffect(() => {
    if (!isAuthenticated) {
      setIsWarningOpen(false);
      isWarningOpenRef.current = false;
      return;
    }

    const idleWarningThreshold = effectiveTimeoutSeconds - effectiveWarningSeconds;

    intervalTimerRef.current = setInterval(() => {
      // 1. If user is viewing a payment callback or receipt, pause timeout
      if (isPaymentCallbackRoute()) {
        recordActivity();
        if (isWarningOpenRef.current) {
          setIsWarningOpen(false);
          isWarningOpenRef.current = false;
          setCountdown(effectiveWarningSeconds);
        }
        return;
      }

      // 2. If a financial transaction/API request is in-flight, defer timeout
      if (hasInFlightMutation()) {
        recordActivity();
        if (isWarningOpenRef.current) {
          setIsWarningOpen(false);
          isWarningOpenRef.current = false;
          setCountdown(effectiveWarningSeconds);
        }
        return;
      }

      // 3. Measure real elapsed wall-clock idle time
      const lastActive = getLastActiveTime();
      const idleSeconds = Math.floor((Date.now() - lastActive) / 1000);

      // 4. Time to show warning modal
      if (idleSeconds >= idleWarningThreshold && idleSeconds < effectiveTimeoutSeconds) {
        const remaining = Math.max(0, effectiveTimeoutSeconds - idleSeconds);
        setIsWarningOpen(true);
        isWarningOpenRef.current = true;
        setCountdown(remaining);
      } else if (idleSeconds >= effectiveTimeoutSeconds) {
        // 5. Session expired!
        handleSessionTimeout();
      } else if (isWarningOpenRef.current && idleSeconds < idleWarningThreshold) {
        // Activity occurred in another tab, dismiss warning
        setIsWarningOpen(false);
        isWarningOpenRef.current = false;
        setCountdown(effectiveWarningSeconds);
      }
    }, 1000);

    return () => {
      if (intervalTimerRef.current) {
        clearInterval(intervalTimerRef.current);
        intervalTimerRef.current = null;
      }
    };
  }, [
    isAuthenticated,
    effectiveTimeoutSeconds,
    effectiveWarningSeconds,
    isPaymentCallbackRoute,
    hasInFlightMutation,
    handleSessionTimeout,
    getLastActiveTime,
    recordActivity,
  ]);

  return {
    isWarningOpen,
    countdown,
    effectiveWarningSeconds,
    effectiveTimeoutSeconds,
    isRefreshing,
    handleStayLoggedIn,
    handleLogoutNow: handleSessionTimeout,
    resetTimer,
  };
};

export default useSessionTimeout;
